import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchBlogPost } from '../utils/blogUtils';
import { linkDishMentions } from '../utils/linkDishMentions';
import { BUSINESS_ID, SITE_URL, business } from '../data/business';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

interface BlogPostMetadata {
  id: string;
  title: string;
  description: string;
  publishDate: string;
  formattedDate: string;
  imageUrl: string | null;
  url: string;
}

const BlogPost = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [metadata, setMetadata] = useState<BlogPostMetadata | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const { metadata, content } = await fetchBlogPost(id);
        
        if (!metadata || !content) {
          setError('Blog post not found');
          return;
        }
        
        setMetadata(metadata);
        setContent(content);
      } catch (error) {
        console.error('Error loading blog post:', error);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };
    
    loadPost();
  }, [id]);
  
  const displayTitle = metadata?.title.replace(' | Thaprachan Thai', '') || '';

  const absoluteImage = metadata?.imageUrl
    ? (metadata.imageUrl.startsWith('http') ? metadata.imageUrl : `${SITE_URL}${metadata.imageUrl}`)
    : `${SITE_URL}/assets/images/optimized/ThaprachanLogo.webp`;

  /** Dates come from the post's own article:published_time meta tag. */
  const articleSchema = metadata && {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: displayTitle,
    description: metadata.description,
    url: `${SITE_URL}/blog/${id}`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${id}` },
    ...(metadata.publishDate ? { datePublished: metadata.publishDate } : {}),
    ...(metadata.imageUrl ? { image: absoluteImage } : {}),
    author: { '@type': 'Organization', name: business.name, '@id': BUSINESS_ID },
    publisher: { '@id': BUSINESS_ID },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: displayTitle, item: `${SITE_URL}/blog/${id}` },
    ],
  };
  
  return (
    <>
      <Helmet>
        {metadata && (
          <>
            <title>{metadata.title}</title>
            <meta name="description" content={metadata.description} />
            {/* Was pointing at thaprachanthairestaurant.com — a different
                domain. A cross-domain canonical tells Google to index that URL
                instead of this one, which keeps the post out of search. */}
            <link rel="canonical" href={`${SITE_URL}/blog/${id}`} />

            <meta property="og:site_name" content={business.name} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={metadata.title} />
            <meta property="og:description" content={metadata.description} />
            <meta property="og:url" content={`${SITE_URL}/blog/${id}`} />
            <meta property="og:image" content={absoluteImage} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={metadata.title} />
            <meta name="twitter:description" content={metadata.description} />
            <meta name="twitter:image" content={absoluteImage} />

            <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
          </>
        )}
      </Helmet>
      
      <Header />
      
      <main className="pt-24 pb-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-lg text-gray-500">Loading blog post...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 border rounded-lg">
              <p className="text-lg text-gray-500">{error}</p>
              <Link 
                to="/blog"
                className="inline-block mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
              >
                Back to Blog
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <Link 
                  to="/blog"
                  className="text-primary hover:text-primary/80 inline-flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Blog
                </Link>
              </div>
              
              <article className="prose lg:prose-xl mx-auto">
                {metadata?.imageUrl && (
                  <img 
                    src={metadata.imageUrl} 
                    alt={displayTitle}
                    className="w-full h-auto rounded-lg mb-6 object-cover" 
                  />
                )}
                
                <div className="mb-4">
                  <p className="text-sm text-primary-ink mb-2">{metadata?.formattedDate}</p>
                  <h1 className="text-3xl md:text-4xl font-bold font-serif mb-6 text-gray-900">
                    {displayTitle}
                  </h1>
                </div>
                
                <div 
                  className="blog-content"
                  dangerouslySetInnerHTML={{ __html: linkDishMentions(content || '') }} 
                />
              </article>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default BlogPost; 